/**
 * smc/scoring.ts
 * Bias determination, premium/discount analysis, and SMC confidence scoring.
 */
import { Candle, FairValueGap, LiquiditySweep, OrderBlock, PremiumDiscount, Displacement, StructureEvent, SMCBias, SMCStrength } from '../../../../types';
import { SwingPoint } from './types';

/**
 * The "dealing range" is defined as the active session range between the
 * most recent significant swing high and swing low.
 *
 * Premium zone (above equilibrium): optimal area to SELL / look for shorts
 * Discount zone (below equilibrium): optimal area to BUY / look for longs
 * Equilibrium: 50% retracement of the range
 */
export function analyzePremiumDiscount(
  candles: Candle[],
  swingHighs: SwingPoint[],
  swingLows: SwingPoint[]
): PremiumDiscount | null {
  if (!swingHighs.length || !swingLows.length) return null;

  const recentHigh = [...swingHighs].filter(s => s.idx >= candles.length - 40).sort((a, b) => b.price - a.price)[0];
  const recentLow  = [...swingLows].filter(s => s.idx >= candles.length - 40).sort((a, b) => a.price - b.price)[0];

  if (!recentHigh || !recentLow || recentHigh.price <= recentLow.price) return null;

  const rangeHigh    = recentHigh.price;
  const rangeLow     = recentLow.price;
  const equilibrium  = (rangeHigh + rangeLow) / 2;
  const currentPrice = candles[candles.length - 1].close;
  const rangeSize    = rangeHigh - rangeLow;
  const positionPct  = ((currentPrice - rangeLow) / rangeSize) * 100;

  let zone: PremiumDiscount['zone'];
  let description: string;

  if (positionPct >= 62.5) {
    zone = 'premium';
    description = `Price in premium zone (${positionPct.toFixed(0)}% of dealing range): above equilibrium ${equilibrium.toFixed(2)}. Optimal area for short entries; long entries carry higher risk.`;
  } else if (positionPct <= 37.5) {
    zone = 'discount';
    description = `Price in discount zone (${positionPct.toFixed(0)}% of dealing range): below equilibrium ${equilibrium.toFixed(2)}. Optimal area for long entries; short entries carry higher risk.`;
  } else {
    zone = 'equilibrium';
    description = `Price near equilibrium (${positionPct.toFixed(0)}% of dealing range): in the middle of the dealing range. Neither premium nor discount — wait for displacement toward extremes.`;
  }

  return { rangeHigh, rangeLow, equilibrium, currentPrice, zone, positionPct, description };
}

export function determineBias(
  swingHighs: SwingPoint[],
  swingLows: SwingPoint[],
  structureEvents: StructureEvent[],
  candles: Candle[]
): { bias: SMCBias; strength: SMCStrength } {
  if (!swingHighs.length || !swingLows.length) return { bias: 'neutral', strength: 'minor' };

  const recentHighs = swingHighs.slice(-4);
  const recentLows  = swingLows.slice(-4);

  const recentCHOCH = structureEvents.find(e => e.type === 'CHOCH' && e.candleIdx >= candles.length - 8);
  if (recentCHOCH) {
    const chochDir = recentCHOCH.dir;
    const lastHighSeq = recentHighs.length >= 2 && recentHighs[recentHighs.length-1].price > recentHighs[recentHighs.length-2].price;
    const lastLowSeq  = recentLows.length >= 2  && recentLows[recentLows.length-1].price > recentLows[recentLows.length-2].price;
    const chochCorroborated =
      (chochDir === 'bullish' && (lastHighSeq || lastLowSeq)) ||
      (chochDir === 'bearish' && (!lastHighSeq || !lastLowSeq));
    if (chochCorroborated) {
      return { bias: chochDir, strength: 'minor' };
    }
  }

  const hh1 = recentHighs.length >= 3 && recentHighs[recentHighs.length-1].price > recentHighs[recentHighs.length-2].price;
  const hh2 = recentHighs.length >= 3 && recentHighs[recentHighs.length-2].price > recentHighs[recentHighs.length-3].price;
  const hl1 = recentLows.length >= 3  && recentLows[recentLows.length-1].price > recentLows[recentLows.length-2].price;
  const hl2 = recentLows.length >= 3  && recentLows[recentLows.length-2].price > recentLows[recentLows.length-3].price;
  const lh1 = recentHighs.length >= 3 && recentHighs[recentHighs.length-1].price < recentHighs[recentHighs.length-2].price;
  const lh2 = recentHighs.length >= 3 && recentHighs[recentHighs.length-2].price < recentHighs[recentHighs.length-3].price;
  const ll1 = recentLows.length >= 3  && recentLows[recentLows.length-1].price < recentLows[recentLows.length-2].price;
  const ll2 = recentLows.length >= 3  && recentLows[recentLows.length-2].price < recentLows[recentLows.length-3].price;

  if (hh1 && hh2 && hl1 && hl2) return { bias: 'bullish', strength: 'major' };
  if (lh1 && lh2 && ll1 && ll2) return { bias: 'bearish', strength: 'major' };

  const makingHigherHighs = recentHighs.length >= 2 && recentHighs[recentHighs.length-1].price > recentHighs[recentHighs.length-2].price;
  const makingHigherLows  = recentLows.length >= 2  && recentLows[recentLows.length-1].price > recentLows[recentLows.length-2].price;
  const makingLowerHighs  = recentHighs.length >= 2 && recentHighs[recentHighs.length-1].price < recentHighs[recentHighs.length-2].price;
  const makingLowerLows   = recentLows.length >= 2  && recentLows[recentLows.length-1].price < recentLows[recentLows.length-2].price;

  if (makingHigherHighs && makingHigherLows) return { bias: 'bullish', strength: 'minor' };
  if (makingLowerHighs  && makingLowerLows)  return { bias: 'bearish', strength: 'minor' };

  return { bias: 'neutral', strength: 'minor' };
}

export function calcSMCConfidence(
  bias: SMCBias,
  biasStrength: SMCStrength,
  lastBOS: StructureEvent | null,
  lastCHOCH: StructureEvent | null,
  recentSweep: LiquiditySweep | null,
  recentDisplacement: Displacement | null,
  fvgBelow: FairValueGap | null,
  fvgAbove: FairValueGap | null,
  obBelow: OrderBlock | null,
  obAbove: OrderBlock | null,
  premiumDiscount: PremiumDiscount | null,
  candles: Candle[]
): number {
  let score = 30;

  if (bias !== 'neutral') score += biasStrength === 'major' ? 20 : 10;
  if (lastBOS && lastBOS.candleIdx >= candles.length - 5) score += 15;
  if (lastCHOCH && lastCHOCH.candleIdx >= candles.length - 8) score += 12;
  if (recentSweep?.reclaimed && recentSweep.candleIdx >= candles.length - 5) score += 18;
  else if (recentSweep && recentSweep.candleIdx >= candles.length - 8) score += 8;
  if (recentDisplacement && recentDisplacement.candleIdx >= candles.length - 5) score += 12;

  const curPrice = candles[candles.length - 1].close;
  if (fvgBelow && (curPrice - fvgBelow.top) / curPrice < 0.005) score += 8;
  if (fvgAbove && (fvgAbove.bottom - curPrice) / curPrice < 0.005) score += 8;
  if (obBelow && (curPrice - obBelow.top) / curPrice < 0.008) score += 8;
  if (obAbove && (obAbove.bottom - curPrice) / curPrice < 0.008) score += 8;

  if (premiumDiscount) {
    const pz = premiumDiscount.zone;
    const aligned = (bias === 'bullish' && pz === 'discount') || (bias === 'bearish' && pz === 'premium');
    if (aligned) score += 10;
  }

  return Math.min(92, Math.max(0, score));
}
